import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  AfterViewInit,
  viewChild,
  input,
  signal,
  inject,
  OnInit,
  effect,
  Output,
  EventEmitter,
} from '@angular/core';
import { ButtonsModule, InputModule, UtilsModule } from 'axis-components';
import {
  AxisFile,
  FileUploadModule,
  UploadComponent,
} from 'axis-components/file-upload';
import { ListComponent, ListModule } from 'axis-components/list';
import { TabsModule } from 'axis-components/tab';
import SignaturePad from 'signature_pad';
import { SignatureService } from '../../_services/uim-ui/signature.service';
import { FormControl, FormGroup } from '@angular/forms';

type SignatureMethod = 'type' | 'draw' | 'file';

type SignatureData = {
  method: SignatureMethod;
  drawDataUrl: string;
  uploadFileSrc: string;
  typedText: string;
};

@Component({
  selector: 'app-signature',
  standalone: true,
  imports: [
    TabsModule,
    ButtonsModule,
    FileUploadModule,
    CommonModule,
    ListModule,
    InputModule,
    UtilsModule,
  ],
  templateUrl: './signature.component.html',
  styleUrl: './signature.component.scss',
})
export class SignatureComponent implements OnInit, AfterViewInit {
  @Output() signatureChange = new EventEmitter<void>();
  signatureService = inject(SignatureService);
  signatureUIData: any;
  readonly canvasRef = viewChild('canvas', {
    read: ElementRef<HTMLCanvasElement>,
  });
  
  signatureForm: FormGroup;
  private signaturePad!: SignaturePad;
  currentDateTime = input<Date>(new Date());
  readonly fileUploadData = viewChild<UploadComponent>('fileUpload');
  fileSrc = signal<string>('');
  selectedMethod = signal<SignatureMethod>('type');
  effect = effect(() => {
    if (this.signatureForm) {
      this.signatureForm.controls['selectedMethod'].setValue(
        this.selectedMethod(),
      );
    }
  });
 
  constructor() {}

  ngOnInit(): void {

    this.signatureService
      .getSignatureData()
      .subscribe((data) => (this.signatureUIData = data));
    this.signatureForm = new FormGroup({
      unSignInputText: new FormControl(''),
      selectedMethod: new FormControl<SignatureMethod>('type'),
      unSignInputDraw: new FormControl(''),
      unSignInputImg: new FormControl(''),
    });
  }

  onTabChange(index: number) {
    const methods: SignatureMethod[] = ['type', 'draw', 'file'];
    this.clearSignature();
    this.selectedMethod.set(methods[index]);
    this.notifySignatureChange();
  }

  listChange(event: AxisFile[]) {
    this.fileSrc.set(
      (event[0]?.src.changingThisBreaksApplicationSecurity as string) || '',
    );
    this.notifySignatureChange();
  }

  ngAfterViewInit() {
    this.signaturePad = new SignaturePad(this.canvasRef().nativeElement, {
      minWidth: 1,
      maxWidth: 2.5,
      throttle: 16,
      velocityFilterWeight: 0.7,
    });
    this.signaturePad.addEventListener('endStroke',() =>{
      this.notifySignatureChange();
    })
  }
  private notifySignatureChange(): void{
   this.signatureChange.emit();
    }    

  get signatureData(): { method: SignatureMethod; data: string } {
    const method = this.selectedMethod();
    let data: string = '';
    if (method === 'draw') {
      if(this.signaturePad && this.signaturePad.isEmpty()){
        data = '';
      }
      else{
            data = this.signaturePad?.toDataURL() || '';
      }

    } else if (method === 'file') {
      data = this.fileSrc();
    } else if (method === 'type') {
      data = this.signatureForm.controls['unSignInputText'].value || '';
    }
    return {
      method,
      data,
    };
  }

  protected clearSignature() {
    switch (this.selectedMethod()) {
      case 'draw':
        this.signaturePad.clear();
        break;
      case 'file':
        this.fileUploadData().delete(
          this.fileUploadData()?.uploadFileList?.[0],
        );
        this.fileSrc.set('');
        break;
      case 'type':
        this.signatureForm.controls['unSignInputText'].setValue('');
        this.signatureForm.controls['unSignInputText'].markAsUntouched();
        break;
    }
  }
}
